import { FontIcon, IBreadcrumbItem, IBreadcrumbProps, PrimaryButton, Stack } from "@fluentui/react";
import { makeStyles } from "@fluentui/react-components";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Box from "@mui/material/Box";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { logException, logPageView } from "../../helper/AppInsightsHelper";
import { CustomBreadcrumb } from "../../SharedComponents/CustomBreadcrumb";
import { useAppSelector } from "../../store/hooks";
import { isValidJson } from "../../Utils/js-common";
import { RECENTLY_VIEWED_LIMIT, VIEWED_KEY } from "../ReportCatalog/ReportCatalog.common";
import {
  useGetFavoriteReportsByLoginQuery,
  useGetReportDetailsQuery,
  useInsertOrUpdateFavoriteReportsMutation,
  useInsertRecentlyViewedReportMutation
} from "../../slices/ExploreReports.slice";

const style = {
  "--font-family": "\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
  margin: 0
};

const pbiReportStyles = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -43%)",
  width: "100%",
  bgcolor: "background.paper",
  height: "100%"
};

const breadcrumbStyles = {
  root: {
    fontSize: 14,
    margin: 0,
    paddingLeft: 8,
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};

const useEmbedPowerBiStyles = makeStyles({
  powerBi: {
    "& iframe": {
      border: "none"
    },
    flexGrow: 1,
    height: "100%"
  }
});

const key = "report-catalog-bc";

export default function FullPageReportView() {
  const navigate = useNavigate();
  const [insertRecentlyViewedReport] = useInsertRecentlyViewedReportMutation();
  const styles = useEmbedPowerBiStyles();
  const { reportId } = useParams<{ reportId: string }>();
  const formattedReportId = reportId?.split("=")[1];
  const location = useLocation();
  const { reportName, reportLink } = location.state || {};

  const { data, error, isLoading } = useGetReportDetailsQuery(reportId);
  const [reportDetails, setReportDetails] = React.useState({ reportName, reportLink });
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  const [isFavorite, setIsFavorite] = useState(false);
  const [insertOrUpdateFavoriteReport] = useInsertOrUpdateFavoriteReportsMutation();
  const { data: favoriteReportsData, error: favoriteReportsError, refetch } = useGetFavoriteReportsByLoginQuery({});

  const commonColor: string = "hsl(206, 100%, 42%)";
  const LoadFavoriteIcon = (props: {
    favIcon: boolean;
    updateFavIcon: (status: boolean) => void;
    onFavoriteChange: (isFavorite: boolean) => void;
  }) => {
    return (
      <Stack style={{ display: "inline-flex" }}>
        {props.favIcon ? (
          <FavoriteIcon
            titleAccess="Click to unfavorite this report"
            sx={{
              color: commonColor,
              fontSize: "1.4rem"
            }}
            style={{ cursor: "pointer" }}
            onClick={() => {
              props.updateFavIcon(false);
              props.onFavoriteChange(false);
            }}
          />
        ) : (
          <FavoriteBorderIcon
            titleAccess="Click to favorite this report"
            sx={{
              color: commonColor,
              fontSize: "1.4rem"
            }}
            style={{ cursor: "pointer" }}
            onClick={() => {
              props.updateFavIcon(true);
              props.onFavoriteChange(true);
            }}
          />
        )}
      </Stack>
    );
  };

  const handleFavoriteChange = (isFavorite: boolean) => {
    insertOrUpdateFavoriteReport({
      report: reportDetails?.reportName ?? "",
      loginName: userName ?? "",
      loginEmail: userEmail ?? "",
      isFavorite: Number(isFavorite)
    }).then(() => {
      refetch();
    });
  };

  useEffect(() => {
    refetch();
  }, [location]);

  useEffect(() => {
    if (favoriteReportsData && Array.isArray(favoriteReportsData)) {
      const reportData = favoriteReportsData.find((report) => report.report === reportDetails?.reportName);
      if (reportData) {
        setIsFavorite(reportData.isFavorite);
      }
    } else if (favoriteReportsError) {
      const customProps = {
        ReportId: reportId,
        ReportName: reportDetails?.reportName,
        ReportLink: reportDetails?.reportLink
      };
      logException(
        "FullPage Report View Page",
        "FullPageReportView",
        "getFavoriteReportsByLogin",
        favoriteReportsError,
        customProps
      );
    }
  }, [favoriteReportsData, favoriteReportsError, reportDetails?.reportName]);

  useEffect(() => {
    if (!reportDetails.reportName && !reportDetails.reportLink && data && data.length > 0) {
      setReportDetails({
        reportName: data[0].report,
        reportLink: data[0].reportLink
      });
    }
    if (error) {
      const customProps = {
        ReportId: reportId,
        ReportName: reportDetails?.reportName,
        ReportLink: reportDetails?.reportLink
      };
      logException("FullPage Report View Page", "FullPageReportView", "useGetReportDetailsQuery", error, customProps);
    }
  }, [data, error, reportDetails]);

  // This code stores recently viewed report Id's in session storage, maintaining a list upto 10 unique entries.
  // New reports are added to the top, duplicates are avoided, and each new entry is also sent to the API.
  useEffect(() => {
    if (reportId) {
      const viewedKey = VIEWED_KEY;
      const checkExisting = sessionStorage.getItem(viewedKey);
      const MAX_RECENTLY_VIEWED = RECENTLY_VIEWED_LIMIT;
      let viewedList: string[] = [];
      try {
        viewedList = checkExisting && isValidJson(checkExisting) ? JSON.parse(checkExisting) : [];
      } catch (e) {
        console.error(
          "Failed to parse recently viewed reports from sessionStorage.Expected a JSON array of strings.",
          e
        );
      }
      if (formattedReportId && !viewedList?.includes(formattedReportId)) {
        viewedList.unshift(formattedReportId);
        if (viewedList.length > MAX_RECENTLY_VIEWED) {
          viewedList.pop();
        }
        try {
          sessionStorage.setItem(viewedKey, JSON.stringify(viewedList));
        } catch (e) {
          console.error("Failed to save recently viewed reports to sessionStorage", e);
        }
        insertRecentlyViewedReport({
          LoginName: userName ?? "",
          LoginEmail: userEmail ?? "",
          ReportId: formattedReportId,
          Report: reportDetails?.reportName
        });
      }
    }
    // Dependencies:
    // - reportId: Ensures the effect runs whenever the report ID changes, allowing updates to the recently viewed reports.
    // - reportDetails: Ensures the effect runs whenever report details change, enabling accurate tracking of the report's metadata.
  }, [reportId, reportDetails]);

useEffect(() => {
  if (formattedReportId && reportDetails?.reportName) {
    const customProps = {
      ReportId: formattedReportId,
      ReportName: reportDetails?.reportName
    };
    logPageView("FullPage Report View Page", window.location.href, customProps);
  }
}, [formattedReportId, reportDetails?.reportName]);
  const items: IBreadcrumbItem[] = [
    { text: "Home", key: "home", onClick: () => navigate("/") },
    { text: "Explore Reports", key: "exploreReports", onClick: () => navigate("/explorereports") },
    { text: reportDetails.reportName, key: "reportName", isCurrentItem: true }
  ];

  const bcrops: IBreadcrumbProps = {
    items: items,
    styles: breadcrumbStyles
  };

  const closeReport = () => {
    navigate("/explorereports");
  };

  window.onerror = function () {
    // Do nothing in this case
    // This error is thrown by PowerBI client library when user don't have access to the report
    // or if there is any issue in opening the report.
    // This is not a critical or blocking error for Security360, so we can ignore it.

    return false;
  };

  return (
    <div className="mainCard" style={style}>
      {isLoading || !reportDetails.reportName ? (
        ""
      ) : (
        <Stack horizontal style={{ position: "relative" }}>
          <div style={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            <CustomBreadcrumb {...bcrops} key={key} />
            <LoadFavoriteIcon
              favIcon={isFavorite}
              updateFavIcon={setIsFavorite}
              onFavoriteChange={handleFavoriteChange}
            />
          </div>
          <PrimaryButton
            style={{
              position: "absolute",
              right: 140,
              marginRight: "10px",
              marginTop: "10px"
            }}
            text="Open In New Window"
            type="a"
            href={reportDetails.reportLink}
            target="_blank"
          >
            <FontIcon
              aria-label="OpenInNewWindowButton"
              iconName="OpenInNewWindow"
              style={{ fontSize: "20px", marginLeft: "5px" }}
            />
          </PrimaryButton>
          <PrimaryButton
            style={{
              position: "absolute",
              right: 30,
              marginRight: "10px",
              marginTop: "10px"
            }}
            text="Close"
            onClick={() => {
              closeReport();
            }}
          >
            <FontIcon
              aria-label="ReportCloseButton"
              iconName="ChromeClose"
              style={{ fontSize: "10px", marginLeft: "5px" }}
            />
          </PrimaryButton>
        </Stack>
      )}
      <Box sx={pbiReportStyles}>
        <PowerBIEmbed
          cssClassName={styles.powerBi}
          embedConfig={{
            type: "report", // Supported types: report, dashboard, tile, visual, qna, paginated report and create
            id: formattedReportId,
            embedUrl: `https://msit.powerbi.com/reportEmbed?reportId=${formattedReportId}`,
            accessToken: useAppSelector((state) => state.auth.powerbiToken),
            tokenType: 0, // Use models.TokenType.Aad for SaaS embed
            settings: {
              customLayout: {
                displayOption: models.DisplayOption.FitToPage
              },
              panes: {
                pageNavigation: {
                  position: models.PageNavigationPosition.Left
                }
              }
            }
          }}
        />
      </Box>
    </div>
  );
}
